import { useState } from 'react';

import { api, ApiClientError } from '../lib/apiClient.js';
import { useSite } from '../context/SiteProvider.jsx';
import { usePageMeta } from '../hooks/usePageMeta.js';
import { Reveal } from '../components/common/Reveal.jsx';
import { RichText } from '../components/common/RichText.jsx';
import { OrderSummaryCard, OrderTimeline, StatusBadge } from '../components/site/OrderSummary.jsx';
import { formatDate } from '../lib/format.js';

export default function TrackOrder() {
  const { t, language } = useSite();
  usePageMeta(t('trackButton'));

  const [form, setForm] = useState({ orderNumber: '', phone: '' });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setError(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setOrder(null);
    try {
      const result = await api.post('/orders/track', form);
      setOrder(result.order);
    } catch (cause) {
      setError(cause instanceof ApiClientError && cause.code === 'ORDER_NOT_FOUND' ? 'trackNotFound' : 'errorGeneric');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main id="main">
      <section className="order-page section-pad">
        <Reveal className="order-intro">
          <p className="eyebrow">{t('trackEyebrow')}</p>
          <RichText as="h2" html={t('trackTitle')} />
          <p>{t('trackText')}</p>

          <form className="order-lookup" onSubmit={submit}>
            <label>
              <span>{t('trackNumberLabel')}</span>
              <input
                value={form.orderNumber}
                onChange={update('orderNumber')}
                placeholder="CNX-100001"
                required
              />
            </label>
            <label>
              <span>{t('trackPhoneLabel')}</span>
              <input type="tel" value={form.phone} onChange={update('phone')} required />
            </label>
            {error ? <em className="field-error">{t(error)}</em> : null}
            <button className="button dark" type="submit" disabled={isLoading}>
              <span>{isLoading ? t('loading') : t('trackButton')}</span>
              <span>↗</span>
            </button>
          </form>
        </Reveal>

        {order ? (
          <Reveal className="order-detail">
            <div className="order-status-row">
              <div>
                <span>{t('orderNumberLabel')}</span>
                <b>{order.orderNumber}</b>
              </div>
              <div>
                <span>{t('orderStatus')}</span>
                <StatusBadge status={order.status} />
              </div>
              <div>
                <span>{t('orderDate')}</span>
                <b>{formatDate(order.placedAt, language)}</b>
              </div>
            </div>
            <OrderTimeline timeline={order.timeline} />
            <OrderSummaryCard order={order} />
          </Reveal>
        ) : null}
      </section>
    </main>
  );
}
